import type { ChangeEvent, ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { styled, css } from 'styled-components';
import { isNonEmptyString } from '@wistia/type-guards';
import { FormControlLabel } from '../../private/components/FormControlLabel';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';

export type SwitchSizeType = 'lg' | 'md' | 'sm';

const switchHeightMap = {
  sm: 16,
  md: 20,
  lg: 24,
};

const getSizeCss = (size: SwitchSizeType) => css`
  --wui-switch-height: ${switchHeightMap[size]}px;
`;

const StyledSwitchWrapper = styled.div<{
  $disabled: boolean;
  $size: SwitchSizeType;
  $hideLabel: boolean;
}>`
  ${({ $size }) => getSizeCss($size)}
  --wui-switch-border-offset: 2px;
  --wui-switch-background-color: var(--wui-color-bg-surface-tertiary);
  --wui-switch-border-color: transparent;
  --wui-switch-thumb-color: var(--wui-color-icon-on-fill);
  --wui-switch-thumb-margin-left: 0px;
  --wui-switch-thumb-size: calc(var(--wui-switch-height) - var(--wui-switch-border-offset) * 2);
  --wui-switch-width: calc(var(--wui-switch-thumb-size) * 2 + var(--wui-switch-border-offset) * 2);
  --wui-switch-thumb-position: var(--wui-switch-border-offset);
  --wui-switch-outline-color: transparent;

  &:has(input:checked) {
    --wui-switch-background-color: var(--wui-color-bg-fill);
    --wui-switch-thumb-position: calc(
      var(--wui-switch-width) - var(--wui-switch-thumb-size) - var(--wui-switch-border-offset)
    );
  }

  &:has(input:disabled) {
    --wui-switch-background-color: var(--wui-color-bg-surface-tertiary);
    --wui-switch-thumb-color: var(--wui-color-bg-surface-disabled);
  }

  &:has(input:focus-visible) {
    --wui-switch-outline-color: var(--wui-color-focus-ring);
  }

  display: flex;
  margin: 0;
  position: relative;

  /* TODO this solves a problem but potentially causes and a11y issue */
  user-select: none;
`;

const StyledSwitchTrack = styled.div`
  width: var(--wui-switch-width);
  height: var(--wui-switch-height);
  min-width: var(--wui-switch-width);
  min-height: var(--wui-switch-height);
  border-radius: var(--wui-border-radius-rounded);
  display: flex;
  align-items: center;
  outline-width: 2px;
  outline-offset: 2px;
  outline-style: solid;
  outline-color: var(--wui-switch-outline-color);
  transition:
    background-color var(--wui-motion-duration-03) var(--wui-motion-ease-out),
    outline-color var(--wui-motion-duration-03) var(--wui-motion-ease-out);
  background-color: var(--wui-switch-background-color);
  margin-top: calc(calc(1lh - var(--wui-switch-height)) / 2);
`;

const StyledSwitchThumb = styled.div`
  border-radius: var(--wui-border-radius-rounded);
  background-color: var(--wui-switch-thumb-color);
  transition:
    margin var(--wui-motion-duration-03) var(--wui-motion-ease-out),
    background-color var(--wui-motion-duration-03) var(--wui-motion-ease-out);
  width: var(--wui-switch-thumb-size);
  height: var(--wui-switch-thumb-size);
  margin-left: var(--wui-switch-thumb-position);
`;

const StyledHiddenSwitchInput = styled.input`
  ${visuallyHiddenStyle}
`;

export type SwitchProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'hideLabel' | 'label' | 'size'
> & {
  /**
   * The alignment of the switch relative to the label
   */
  align?: 'left' | 'right';
  /**
   * Indicates the state of the switch
   */
  checked?: boolean;
  /**
   * Disables switch
   */
  disabled?: boolean;
  /**
   * Optional ID attribute
   */
  id?: string | undefined;
  /**
   * The label for the switch
   */
  label: ReactNode;
  /**
   * The description for the switch
   */
  description?: ReactNode | undefined;
  /**
   * The name of the switch. Submitted with its owning form as part of a name/value pair.
   */
  name?: string | undefined;
  /**
   * Callback function invoked when the value changes
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Indicates if the input is required
   */
  required?: boolean;
  /**
   * A predefined size for the switch
   */
  size?: SwitchSizeType;
  /**
   * The value given as data when submitted with a `name`.
   */
  value?: string;
  /**
   * Whether to visually hide the label (it will still be available to screen readers)
   */
  hideLabel?: boolean;
};

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      align = 'left',
      checked,
      disabled = false,
      id,
      label,
      description,
      name,
      onChange,
      size = 'md',
      value,
      required = false,
      hideLabel = false,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const computedId = isNonEmptyString(id) ? id : `wistia-ui-switch-${generatedId}`;

    return (
      <StyledSwitchWrapper
        $disabled={disabled}
        $hideLabel={hideLabel}
        $size={size}
      >
        <StyledHiddenSwitchInput
          {...props}
          ref={ref}
          checked={checked}
          disabled={disabled}
          id={computedId}
          name={name}
          onChange={onChange}
          required={required}
          type="checkbox"
          value={value}
        />
        <FormControlLabel
          align={align}
          controlSlot={
            <StyledSwitchTrack
              data-testid="wui-faux-input"
              data-wui-faux-input="true"
            >
              <StyledSwitchThumb />
            </StyledSwitchTrack>
          }
          description={description}
          disabled={disabled}
          hideLabel={hideLabel}
          htmlFor={computedId}
          label={label}
        />
      </StyledSwitchWrapper>
    );
  },
);

Switch.displayName = 'Switch';
